import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Attack-momentum wave reconstructed from /api/matches/:id/momentum.
 * Home goes up, away goes down — same shape as the bookie trackers. Event
 * markers (goals/corners/cards) sit above the chart, anchored to their minute.
 *
 * Pure SVG, no chart-lib dep (keeps the bundle clean + works with MUI v6).
 */

export interface RatePair { h: number; a: number }
export interface MomentumPoint {
  ts: string;
  minute: number | null;
  totGoals: number | null;
  score: [number, number] | null;
  cum: { datt?: { h: number | null; a: number | null }; sot?: any; soff?: any; poss?: any };
  rate: { datt?: RatePair; shots?: RatePair; att?: RatePair } | null;
}
export interface MomentumData {
  matchID: string;
  league: string | null;
  competitors: string[];
  link: string | null;
  active: boolean;
  series: MomentumPoint[];
  timeline: Array<{ sysname: string; side: 'home' | 'away'; minute: number }>;
}

// 3-point moving average — smooths the ~25s snapshot jitter into a wave like the screenshot.
function smooth(vals: number[]): number[] {
  const out = vals.slice();
  for (let i = 1; i < vals.length - 1; i++) out[i] = (vals[i - 1] + vals[i] + vals[i + 1]) / 3;
  return out;
}

// Small icon+color per event. Matches the bookie style (goal red, corner amber, card yellow/red).
const EVENT_STYLE: Record<string, { color: string; label: string; size: number }> = {
  GOAL:          { color: '#e53935', label: 'G', size: 11 },
  CORNER:        { color: '#fbc02d', label: '•', size: 7  },
  YELLOW_CARD:   { color: '#fdd835', label: '▮', size: 8  },
  RED_CARD:      { color: '#d32f2f', label: '▮', size: 8  },
  OFFSIDE:       { color: '#90a4ae', label: '·', size: 6  },
  SUBSTITUTION:  { color: '#43a047', label: '⇅', size: 8  },
};

const HOME_COLOR = '#42a5f5';
const AWAY_COLOR = '#ff7043';

const EVENT_LABEL_ES: Record<string, string> = {
  GOAL: 'Gol', CORNER: 'Córner', YELLOW_CARD: 'Tarjeta amarilla',
  RED_CARD: 'Tarjeta roja', OFFSIDE: 'Offside', SUBSTITUTION: 'Cambio',
};

export const MomentumChart: React.FC<{ data: MomentumData; width?: number; height?: number }> = ({
  data, width = 720, height = 220,
}) => {
  // Hardened against malformed responses: a missing field never throws.
  const series = Array.isArray(data?.series) ? data.series : [];
  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
  const competitors = Array.isArray(data?.competitors) ? data.competitors : [];

  const padL = 24, padR = 16, padT = 28, padB = 22;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const cy = padT + innerH / 2; // zero line

  // Series with rate; if very short, render an "esperando datos" hint.
  // Downsample to ≤200 points so a long match doesn't ship a huge SVG path
  // string and thrash the main thread (defense in depth alongside the
  // backend's 90-min window cap).
  //
  // CRITICAL: minute must be SANE. The ingester occasionally emits absurd
  // values during halftime when the provider's timer reads weird (we've
  // seen minute=-15372286729 in real prod responses). A single bad value
  // expanded the X domain to billions and the ticks loop iterated 10^29
  // times → Firefox froze on rendering. Filter to a reasonable football
  // range [0, 200] — anything outside is garbage.
  const sane = (m: number | null | undefined): boolean =>
    typeof m === 'number' && Number.isFinite(m) && m >= 0 && m <= 200;
  const raw = series.filter((p) => p.rate && sane(p.minute));
  const step = Math.max(1, Math.ceil(raw.length / 200));
  const pts = step === 1 ? raw : raw.filter((_, i) => i % step === 0 || i === raw.length - 1);
  if (pts.length < 2) {
    return (
      <Box sx={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0f1722', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ color: '#90a4ae' }}>
          {series.length === 0 ? 'Sin snapshots — partido sin cobertura livePanel (~47% de los casos).' : 'Esperando datos (1-2 minutos)…'}
        </Typography>
      </Box>
    );
  }

  // X domain: by minute. (Match clock — same axis as the bookie chart.)
  // timeline.minute is also filtered to the sane range — defense in depth so
  // a single bogus event minute can't blow up maxMin and the ticks loop.
  const minMin = Math.min(...pts.map((p) => p.minute!));
  const saneTimelineMax = timeline.reduce((m, e) => (sane(e.minute) ? Math.max(m, e.minute) : m), 0);
  const maxMin = Math.max(...pts.map((p) => p.minute!), saneTimelineMax);
  const span = Math.max(1, maxMin - minMin);
  const xOf = (m: number) => padL + ((m - minMin) / span) * innerW;

  // Y domain: max combined rate (cap so a single huge delta doesn't squash the wave).
  const homeR = smooth(pts.map((p) => p.rate!.datt?.h ?? 0));
  const awayR = smooth(pts.map((p) => p.rate!.datt?.a ?? 0));
  const yMax = Math.max(1, ...homeR, ...awayR);
  const yOf = (v: number) => (v / yMax) * (innerH / 2);

  // SVG paths (filled areas, mirrored on the center line).
  const homePath =
    `M ${xOf(pts[0].minute!)} ${cy} ` +
    pts.map((p, i) => `L ${xOf(p.minute!)} ${cy - yOf(homeR[i])}`).join(' ') +
    ` L ${xOf(pts[pts.length - 1].minute!)} ${cy} Z`;
  const awayPath =
    `M ${xOf(pts[0].minute!)} ${cy} ` +
    pts.map((p, i) => `L ${xOf(p.minute!)} ${cy + yOf(awayR[i])}`).join(' ') +
    ` L ${xOf(pts[pts.length - 1].minute!)} ${cy} Z`;

  // Latest cumulatives for the legend strip.
  const last = pts[pts.length - 1];
  const lastCum = last.cum;
  const pair = (p: any) => p ? `${p.h ?? '-'}-${p.a ?? '-'}` : '—';

  // X ticks every 15 minutes. Hard-capped at 50 iterations as a final
  // belt-and-suspenders guard: even if some upstream sanitization fails and
  // a garbage minute slips through, this loop CANNOT iterate a billion times
  // and OOM the tab (which is exactly what was happening — see filter above).
  const ticks: number[] = [];
  if (Number.isFinite(minMin) && Number.isFinite(maxMin) && maxMin > minMin) {
    for (let t = Math.ceil(minMin / 15) * 15; t <= maxMin && ticks.length < 50; t += 15) ticks.push(t);
  }

  return (
    <Box sx={{ width, bgcolor: '#0f1722', borderRadius: 1, p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: '#cfd8dc', fontSize: 12 }}>
        <span><span style={{ color: HOME_COLOR }}>● {competitors[0] || 'Home'}</span> &nbsp; <span style={{ color: AWAY_COLOR }}>● {competitors[1] || 'Away'}</span></span>
        <span style={{ color: '#90a4ae' }}>{data?.league || ''}{last.score ? `  ·  ${last.score[0]}-${last.score[1]} (min ${last.minute})` : ''}</span>
      </Box>
      <svg width={width} height={height} role="img" aria-label="momentum chart">
        {/* zero line */}
        <line x1={padL} x2={width - padR} y1={cy} y2={cy} stroke="#37474f" strokeWidth={1} />
        {/* X ticks */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={xOf(t)} x2={xOf(t)} y1={cy - 2} y2={cy + 2} stroke="#546e7a" />
            <text x={xOf(t)} y={height - 6} textAnchor="middle" fontSize={10} fill="#78909c">{t}'</text>
          </g>
        ))}
        {/* Filled waves with hover tooltips per snapshot (invisible hit areas) */}
        <path d={homePath} fill={HOME_COLOR} fillOpacity={0.55} stroke={HOME_COLOR} strokeWidth={1.3} />
        <path d={awayPath} fill={AWAY_COLOR} fillOpacity={0.55} stroke={AWAY_COLOR} strokeWidth={1.3} />
        {/* Hover markers: capped at ~30 across the chart so a long match doesn't
            ship 200+ extra SVG groups (each with a <title> string). Picks 1 every
            ⌈pts/30⌉ points + always the last one. */}
        {(() => {
          const hoverStep = Math.max(1, Math.ceil(pts.length / 30));
          const hoverPts = pts.filter((_, i) => i % hoverStep === 0 || i === pts.length - 1);
          return hoverPts.map((p, i) => {
            const x = xOf(p.minute!);
            const dattH = p.rate!.datt?.h?.toFixed(1) ?? '—';
            const dattA = p.rate!.datt?.a?.toFixed(1) ?? '—';
            const shH = p.rate!.shots?.h?.toFixed(1) ?? '—';
            const shA = p.rate!.shots?.a?.toFixed(1) ?? '—';
            return (
              <g key={`pt-${i}`}>
                <title>
                  {`min ${p.minute}'  ·  ataq.pelig/min: H ${dattH} · A ${dattA}` +
                   `\ntiros/min: H ${shH} · A ${shA}` +
                   (p.score ? `\nmarcador: ${p.score[0]}-${p.score[1]}` : '')}
                </title>
                <circle cx={x} cy={cy} r={6} fill="transparent" stroke="transparent" />
              </g>
            );
          });
        })()}
        {/* Event markers above the chart, with hover tooltip */}
        {timeline.map((e, i) => {
          if (e.minute < minMin || e.minute > maxMin) return null;
          const style = EVENT_STYLE[e.sysname] || { color: '#9e9e9e', label: '·', size: 6 };
          const x = xOf(e.minute);
          const y = padT - 8 + (e.side === 'home' ? -2 : 12);
          const eventLabel = EVENT_LABEL_ES[e.sysname] || e.sysname.toLowerCase();
          const sideLabel = e.side === 'home' ? (competitors[0] || 'Local') : (competitors[1] || 'Visitante');
          return (
            <g key={`ev-${i}`} style={{ cursor: 'help' }}>
              <title>{`${eventLabel} · minuto ${e.minute}' · ${sideLabel}`}</title>
              <circle cx={x} cy={y} r={style.size} fill={style.color} opacity={0.95} />
              <text x={x} y={y + 3.5} textAnchor="middle" fontSize={9} fill="#000" fontWeight={700} pointerEvents="none">{style.label}</text>
            </g>
          );
        })}
      </svg>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, color: '#b0bec5', fontSize: 11, flexWrap: 'wrap' }}>
        <span>ataq.pelig: <strong>{pair(lastCum.datt)}</strong></span>
        <span>tiros arco: <strong>{pair(lastCum.sot)}</strong></span>
        <span>tiros fuera: <strong>{pair(lastCum.soff)}</strong></span>
        <span>posesión %: <strong>{pair(lastCum.poss)}</strong></span>
      </Box>
    </Box>
  );
};
